import { useUser } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import type { Plan } from '@dealcloser/shared'

type UserMeResponse = {
  readonly success: boolean
  readonly data?: {
    readonly user: {
      readonly plan?: string
      readonly is_comp?: number
    }
  }
}

/**
 * Returns the current user's plan.
 * Checks Clerk metadata first, falls back to cached /api/auth/me response (D1).
 * Defaults to 'starter' if neither has a plan set.
 *
 * Friends-and-family override (migration 0024): if the D1 user has
 * `is_comp = 1`, force the effective plan to 'pro' regardless of what
 * Clerk publicMetadata says. The Stripe webhook never updates Clerk metadata
 * for comp users (they have no paying subscription), so Clerk would
 * otherwise report stale 'starter' for them. Backend workers already do this
 * substitution server-side in workers/auth/src/index.ts — this is the
 * matching client-side guarantee so PlanGate / upgrade buttons / feature
 * limits all line up.
 *
 * 2026-06-07: Comp routing changed from 'agency' → 'pro' alongside dropping
 * Agency from the customer-facing PLANS array. Comp users still get a
 * raised daily message cap (100/day vs Pro's 60) via the is_comp check
 * in workers/campaigns/src/daily-tracker.ts checkDailyLimit.
 */
export function usePlan(): Plan {
  const { user } = useUser()
  const queryClient = useQueryClient()

  const cachedMe = queryClient.getQueryData<UserMeResponse>(['user-me'])
  const dbUser = cachedMe?.data?.user

  // Comp override — must beat Clerk metadata because Stripe never updates
  // it for comp users.
  if (dbUser?.is_comp === 1) return 'pro'

  // Primary: Clerk publicMetadata (set by Stripe webhook → Clerk API)
  const clerkPlan = user?.publicMetadata?.plan as Plan | undefined
  if (clerkPlan) return clerkPlan

  // Fallback: D1 plan from cached /api/auth/me (set by DB update). Note that
  // for comp users the auth worker already substitutes the effective plan
  // into user.plan, so even if the is_comp short-circuit above didn't fire
  // this should still return 'pro'.
  const dbPlan = dbUser?.plan as Plan | undefined
  if (dbPlan) return dbPlan

  return 'starter'
}

/**
 * The plan tier to use for LIMIT math (per-campaign lead caps, daily caps).
 * Mirrors resolveLimitsPlan() on the server: comp accounts get Agency-level
 * limits even though usePlan() routes them to 'pro' for UI gating. Using
 * usePlan() for caps made comp/beta testers hit the Pro 60-lead clamp
 * client-side while the server would have accepted everything.
 */
export function useLimitsPlan(): Plan {
  const plan = usePlan()
  const queryClient = useQueryClient()
  const cachedMe = queryClient.getQueryData<UserMeResponse>(['user-me'])
  if (cachedMe?.data?.user?.is_comp === 1) return 'agency'
  return plan
}
